import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from './AuthContext';
import api from '../services/api';

const GuideContext = createContext(undefined);

// Columns that exist in the DynamoDB guides table
// All these columns will be sent to the database
const GUIDE_DB_COLUMNS = [
  'id',
  'title',
  'therapeutic_area',
  'header_option',
  'studies',
  'is_published',
  'is_draft',
  'user_id',
  'created_by_name',
  'thumbnail_url',
  'original_guide_id',
  'event_location',
  'disclaimers',
  'include_medically',
  'medically_url',
  'genentech_access_text',
  'show_society_links',
  'society_label_text',
  'show_author_panel',
  'author_panel_title',
  'authors',
  'society1_name',
  'society1_url',
  'society2_name',
  'society2_url',
  'project_code',
  'created_at',
  'updated_at',
];

// Filter guide data to only include columns that exist in the database
const filterGuideForDB = (guideData) => {
  const filtered = {};
  GUIDE_DB_COLUMNS.forEach(col => {
    if (Object.prototype.hasOwnProperty.call(guideData, col)) {
      filtered[col] = guideData[col];
    }
  });
  return filtered;
};

export const useGuide = () => {
  const context = useContext(GuideContext);
  if (!context) {
    throw new Error('useGuide must be used within a GuideProvider');
  }
  return context;
};

const STUDY_COLORS = {
  LTE: {
    primary: '#0366D3',
    secondary: '#0366D3',
    accent: '#0366D3',
    light: '#DBEAFE',
  },
  RWE: {
    primary: '#DB4505',
    secondary: '#DB4505',
    accent: '#DB4505',
    light: '#FCE7F3',
  },
  BM: {
    primary: '#12284A',
    secondary: '#12284A',
    accent: '#12284A',
    light: '#F3F4F6',
  },
  Ph2: {
    primary: '#AE2185',
    secondary: '#AE2185',
    accent: '#AE2185',
    light: '#FCE7F3',
  },
  Ph3: {
    primary: '#460968',
    secondary: '#460968',
    accent: '#460968',
    light: '#F3F4F6',
  },
};

export const GuideProvider = ({ children }) => {
  const { user, token, isGuestUser, jwtToken } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [publishedGuides, setPublishedGuides] = useState([]);
  const [currentGuide, setCurrentGuideState] = useState(null);
  const [loading, setLoading] = useState(false);

  // Wrapper to ensure studies array always exists when setting current guide
  const setCurrentGuide = useCallback((guide) => {
    if (guide) {
      setCurrentGuideState({
        ...guide,
        studies: guide.studies || [],
      });
    } else {
      setCurrentGuideState(null);
    }
  }, []);

  const fetchDrafts = useCallback(async () => {
    if (!user || !token) return;

    setLoading(true);
    try {
      const data = await api.getDrafts(token, jwtToken, user, isGuestUser);
      setDrafts(data || []);
    } catch (error) {
      console.error('Error fetching drafts:', error);
    } finally {
      setLoading(false);
    }
  }, [user, token, jwtToken, isGuestUser]);

  const fetchPublishedGuides = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const data = await api.getPublished(token, jwtToken, user, isGuestUser);
      setPublishedGuides(data || []);
    } catch (error) {
      console.error('Error fetching published guides:', error);
    } finally {
      setLoading(false);
    }
  }, [token, jwtToken, user, isGuestUser]);

  const createNewGuide = useCallback(() => {
    const newGuide = {
      id: uuidv4(),
      title: '',
      therapeutic_area: 'hematology', // Default to first therapeutic area
      header_option: 1, // Default to first header background for hematology
      studies: [],
      is_published: false,
      is_draft: true,
      user_id: user?.id,
      created_by_name: user?.name || user?.email?.split('@')[0] || 'Unknown',
      created_at: new Date().toISOString(),
      // Note: updated_at is NOT set here - it gets set when the guide is first saved
      // This allows the UI to detect truly new guides that haven't been saved yet
    };
    // console.log('createNewGuide: Creating new guide with id', newGuide.id);
    setCurrentGuideState(newGuide);
    return newGuide;
  }, [user?.id, user?.name, user?.email]);

  const saveGuide = useCallback(async (guideData) => {
    if (!user || !token) return null;

    // Get a proper author name
    // If the current user owns this guide (user_id matches), always use their name
    // Otherwise, preserve the existing creator name if valid
    const existingName = guideData.created_by_name;
    const isValidName = existingName && existingName !== 'Unknown' && existingName !== 'Unknown Author';
    const currentUserOwnsGuide = guideData.user_id === user?.id;
    const currentUserName = user?.name || user?.email?.split('@')[0] || 'You';
    const authorName = currentUserOwnsGuide ? currentUserName : (isValidName ? existingName : currentUserName);

    const updatedGuide = {
      ...guideData,
      created_by_name: authorName,
      updated_at: new Date().toISOString(),
    };

    try {
      // Check if guide exists (has been saved before)
      const existingDraft = drafts.find(d => d.id === guideData.id);

      let data;
      if (existingDraft) {
        // Update existing guide
        data = await api.updateGuide(token, jwtToken, guideData.id, updatedGuide, user, isGuestUser);
      } else {
        // Create new guide
        data = await api.createGuide(token, jwtToken, updatedGuide, user, isGuestUser);
      }

      setCurrentGuide(data);
      await fetchDrafts();
      return data;
    } catch (error) {
      console.error('Error saving guide:', error);
      return null;
    }
  }, [user, token, jwtToken, isGuestUser, setCurrentGuide, fetchDrafts, drafts]);

  const publishGuide = useCallback(async (guideId) => {
    if (!token) return null;

    try {
      const data = await api.publishGuide(token, jwtToken, guideId, user, isGuestUser);
      await fetchDrafts();
      await fetchPublishedGuides();
      return data;
    } catch (error) {
      console.error('Error publishing guide:', error);
      return null;
    }
  }, [token, jwtToken, user, isGuestUser, fetchDrafts, fetchPublishedGuides]);

  const duplicateGuide = useCallback(async (guide) => {
    if (!user || !token) return null;

    // Helper to strip HTML tags for comparison
    const stripHtml = (html) => {
      if (!html) return '';
      return html.replace(/<[^>]*>/g, '').trim();
    };

    // Helper to check if title contains (Copy) pattern
    const extractBaseTitleAndCopyNum = (title) => {
      const stripped = stripHtml(title);
      const copyMatch = stripped.match(/^(.*?)\s*\(Copy(?:\s+(\d+))?\)\s*$/i);
      if (copyMatch) {
        return {
          baseTitle: copyMatch[1].trim(),
          copyNum: copyMatch[2] ? parseInt(copyMatch[2], 10) : 0
        };
      }
      return { baseTitle: stripped, copyNum: null };
    };

    try {
      const data = await api.duplicateGuide(token, jwtToken, guide.id, user, isGuestUser);

      // Fix the title to include original title with (Copy) suffix
      if (data && guide.title) {
        // Get original title without any existing (Copy) suffix
        const { baseTitle: originalBaseTitle } = extractBaseTitleAndCopyNum(guide.title);

        // Fetch current drafts and published guides to check for existing copies
        const currentDrafts = await api.getDrafts(token, jwtToken, user, isGuestUser);
        const currentPublished = await api.getPublished(token, jwtToken, user, isGuestUser);

        // Combine all guides to check for existing copies
        // Exclude the newly created duplicate (data.id) from the check
        const allGuides = [...(currentDrafts || []), ...(currentPublished || [])]
          .filter(g => g.id !== data.id);

        // Find all copies with similar titles to determine the next copy number
        // Track: hasCopyWithoutNumber (for "(Copy)"), and maxNumberedCopy (for "(Copy N)")
        let hasCopyWithoutNumber = false;
        let maxNumberedCopy = 0;
        const baseTitleLower = originalBaseTitle.toLowerCase();

        allGuides.forEach(existingGuide => {
          if (existingGuide.title) {
            const { baseTitle: guideBase, copyNum } = extractBaseTitleAndCopyNum(existingGuide.title);

            // Check if this guide has the same base title with Copy suffix
            if (guideBase.toLowerCase() === baseTitleLower) {
              if (copyNum === 0) {
                // This is "(Copy)" without a number
                hasCopyWithoutNumber = true;
              } else if (copyNum !== null && copyNum > 0) {
                // This is "(Copy N)" - track the highest number
                maxNumberedCopy = Math.max(maxNumberedCopy, copyNum);
              }
            }
          }
        });

        // Generate new title with (Copy) suffix
        // Logic:
        // - If no "(Copy)" or "(Copy N)" exists, create "(Copy)"
        // - If "(Copy)" exists (or original + any copy exists), create "(Copy 1)", "(Copy 2)", etc.
        let copySuffix;
        if (!hasCopyWithoutNumber && maxNumberedCopy === 0) {
          // No copies exist yet - create first copy as "(Copy)"
          copySuffix = '(Copy)';
        } else if (hasCopyWithoutNumber && maxNumberedCopy === 0) {
          // "(Copy)" exists but no numbered copies - next is "(Copy 1)"
          copySuffix = '(Copy 1)';
        } else {
          // Numbered copies exist - increment from the highest
          copySuffix = `(Copy ${maxNumberedCopy + 1})`;
        }

        // Build new title - handle HTML structure if present
        let newTitle;
        const hasHtmlTags = guide.title.includes('<');

        if (hasHtmlTags) {
          // Remove any existing (Copy) suffix from HTML title first
          let cleanHtml = guide.title.replace(/\s*\(Copy(?:\s+\d+)?\)/gi, '');

          // Try to insert before the last closing tag (e.g., </p>)
          const lastClosingTagMatch = cleanHtml.match(/(<\/[^>]+>\s*)$/);
          if (lastClosingTagMatch) {
            const insertPos = cleanHtml.lastIndexOf(lastClosingTagMatch[1]);
            newTitle = cleanHtml.slice(0, insertPos) + ' ' + copySuffix + lastClosingTagMatch[1];
          } else {
            // Fallback: just append to the base title
            newTitle = `<p>${originalBaseTitle} ${copySuffix}</p>`;
          }
        } else {
          newTitle = `${originalBaseTitle} ${copySuffix}`;
        }

        // Fetch the full duplicated guide to get all fields including studies
        const fullGuide = await api.getGuide(token, jwtToken, data.id, user, isGuestUser);

        // Update the duplicated guide with the correct title and ensure creator is set to current user
        const currentUserName = user?.name || user?.email?.split('@')[0] || 'Unknown';
        const guideToSave = {
          ...fullGuide,
          title: newTitle,
          created_by_name: currentUserName,
          updated_at: new Date().toISOString(),
          thumbnail_url: null, // Clear thumbnail so it shows placeholder until regenerated
        };

        // Do a full save like the builder does
        const savedGuide = await api.updateGuide(token, jwtToken, data.id, guideToSave, user, isGuestUser);

        // Use the response from updateGuide which has the confirmed updated data
        if (savedGuide) {
          data.title = savedGuide.title || newTitle;
          data.created_by_name = savedGuide.created_by_name || currentUserName;
          data.thumbnail_url = null; // Clear thumbnail in returned data
        } else {
          data.title = newTitle;
          data.created_by_name = currentUserName;
          data.thumbnail_url = null; // Clear thumbnail in returned data
        }
      }

      // Add a longer delay to allow DynamoDB eventual consistency before fetching
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchDrafts();

      // Also directly add/update the duplicated guide in drafts state to ensure immediate UI update
      setDrafts(prevDrafts => {
        // Check if the guide already exists in drafts (from fetchDrafts)
        const existingIndex = prevDrafts.findIndex(d => d.id === data.id);
        if (existingIndex >= 0) {
          // Update existing entry with correct title
          const updated = [...prevDrafts];
          updated[existingIndex] = { ...updated[existingIndex], ...data };
          return updated;
        } else {
          // Add new entry
          return [data, ...prevDrafts];
        }
      });

      return data;
    } catch (error) {
      console.error('Error duplicating guide:', error);
      return null;
    }
  }, [user, token, jwtToken, isGuestUser, fetchDrafts]);

  const deleteGuide = useCallback(async (guideId) => {
    if (!token) return false;

    try {
      await api.deleteGuide(token, jwtToken, guideId, user, isGuestUser);
      await fetchDrafts();
      return true;
    } catch (error) {
      console.error('Error deleting guide:', error);
      return false;
    }
  }, [token, jwtToken, user, isGuestUser, fetchDrafts]);

  const addStudy = useCallback((studyData) => {
    // Create the new study first with a unique ID
    const newStudy = {
      id: uuidv4(),
      ...studyData,
      isComplete: false,
    };

    // Use functional update to get the latest currentGuide state
    setCurrentGuideState((prevGuide) => {
      if (!prevGuide) {
        console.error('addStudy: No current guide available');
        return prevGuide;
      }

      // Ensure studies array exists
      const existingStudies = prevGuide.studies || [];

      // Set the order based on existing studies
      newStudy.order = existingStudies.length;

      const updatedGuide = {
        ...prevGuide,
        studies: [...existingStudies, newStudy],
      };

      // console.log('addStudy: Adding study to guide', { newStudy, studiesCount: updatedGuide.studies.length });
      return updatedGuide;
    });

    return newStudy;
  }, []);

  const updateStudy = useCallback((studyId, studyData) => {
    setCurrentGuideState((prevGuide) => {
      if (!prevGuide) return prevGuide;
      const existingStudies = prevGuide.studies || [];
      const updatedStudies = existingStudies.map(study =>
        study.id === studyId ? { ...study, ...studyData } : study
      );
      return {
        ...prevGuide,
        studies: updatedStudies,
      };
    });
  }, []);

  const removeStudy = useCallback((studyId) => {
    setCurrentGuideState((prevGuide) => {
      if (!prevGuide) return prevGuide;
      const existingStudies = prevGuide.studies || [];
      const updatedStudies = existingStudies
        .filter(study => study.id !== studyId)
        .map((study, index) => ({ ...study, order: index }));
      return {
        ...prevGuide,
        studies: updatedStudies,
      };
    });
  }, []);

  const reorderStudies = useCallback((newOrder) => {
    setCurrentGuideState((prevGuide) => {
      if (!prevGuide) return prevGuide;
      const existingStudies = prevGuide.studies || [];
      const updatedStudies = newOrder.map((studyId, index) => {
        const study = existingStudies.find(s => s.id === studyId);
        return study ? { ...study, order: index } : null;
      }).filter(Boolean);
      return {
        ...prevGuide,
        studies: updatedStudies,
      };
    });
  }, []);

  const getStudyColors = (studyType) => {
    return STUDY_COLORS[studyType] || STUDY_COLORS.LTE;
  };

  return (
    <GuideContext.Provider
      value={{
        drafts,
        publishedGuides,
        currentGuide,
        loading,
        setCurrentGuide,
        fetchDrafts,
        fetchPublishedGuides,
        createNewGuide,
        saveGuide,
        publishGuide,
        duplicateGuide,
        deleteGuide,
        addStudy,
        updateStudy,
        removeStudy,
        reorderStudies,
        getStudyColors,
        STUDY_COLORS,
      }}
    >
      {children}
    </GuideContext.Provider>
  );
};
